<template>
  <div>
    <div class="app sidebar-fixed sidebar-lg-show" v-bind:class="{ 'dark-mode': currentTheme, 'dev-env': !currentEnv }" v-if="initialized"
      :key="$store.getters.getCurrentProject.uuid"
      :style="isSplitMode && isOpen ? { marginRight: effectiveSplitChatWidth + 'px' } : {}">
      <template v-if="currentWorkspace">

      <AppHeader fixed>
        <b-link class="navbar-brand">
          <Projects @project_changed="reRender()" />
        </b-link>
        <b-button variant="primary" size="sm" class="ml-3" @click="showWorkspacesModal = true">
          <b-icon icon="building"></b-icon> Workspaces
        </b-button>

        <b-navbar-nav class="ml-auto">
          <!-- <b-nav-item class="d-md-down-none notification-bell" to="/admin/scout/events">
            <i class="icon-bell"></i>
            <template v-if="newEvents">
              <b-badge pill variant="danger" v-if="newEvents < 10"> {{ newEvents }}</b-badge>
              <b-badge pill variant="danger" v-else> 9+ </b-badge>
            </template>
          </b-nav-item> -->
          <b-nav-item class="d-md-down-none" v-if="hasKortexAccess">
            <button class="kortex-btn" @click="openCortexChat" :title="$t('kortex.quickAccessHint')" :class="{ 'kortex-helper-active': helperActive && !isOpen }">
              <cortex-icon :size="14" css-class="kortex-btn__icon"></cortex-icon>
              <span class="kortex-btn__label">{{ $t('kortex.openChat') }}</span>
              <kbd class="kortex-kbd-hint">⌘⇧K</kbd>
              <span v-if="helperActive && !isOpen" class="kortex-helper-spark">✦</span>
            </button>
          </b-nav-item>
          <HeaderAlerts />
          <DefaultHeaderDropdownAccnt />
        </b-navbar-nav>
      </AppHeader>

      <div class="app-body">

        <notifications group="system" position="top right" :ignoreDuplicates="true" />
        <notifications group="dataChanged" position="bottom right">f
          <template slot="body">
            <div class="vue-notification" @click="$router.go()">
              {{ $t('dataHasChanged') }}
            </div>
          </template>
        </notifications>
        <main-dialog title="We experienced unexpected error!" id="report-error-modal" ok-title="Send" ok-variant="success"
          @ok="sendErrorReport">
          <b-textarea style="min-height: 100px" :placeholder="this.$t('explainUs')"
            v-model="serverError.comment"></b-textarea>
        </main-dialog>
        
        <!-- Custom Sidebar -->
        <div class="custom-sidebar" :class="{ 'expanded': sidebarExpanded }" @mouseenter="expandSidebar" @mouseleave="handleSidebarMouseLeave">
          <div class="sidebar-custom-nav">
            <nav class="sidebar-nav">
              <ul class="nav">
                <!-- Home and Dashboard first -->
                <template v-for="item in customSidebarNav">
                  <template v-if="item.name === $t('home') || item.name === $t('dashboard')">
                    <li class="nav-item" :key="item.name">
                      <router-link :to="item.url" class="nav-link" :exact="item.name === $t('home')" @click.native="closeSecondarySidebar">
                        <span class="nav-icon nav-icon--svg" v-if="item.icon && item.icon.startsWith('<svg')" v-html="item.icon"></span>
                        <i :class="['nav-icon', item.icon]" v-else-if="item.icon"></i> 
                        <span class="nav-text">{{ item.name }}</span>
                      </router-link>
                    </li>
                  </template>
                </template>
                
                <!-- Components right after Dashboard -->
                <li class="nav-item" @click="openSecondarySidebar('components', $t('components'))">
                  <a class="nav-link" :class="{ 'active': secondarySidebarContent === 'components' }">
                    <i class="nav-icon icon-layers"></i> 
                    <span class="nav-text">{{ $t('components') }}</span>
                  </a>
                </li>
                
                <li class="nav-divider"></li>
                
                <!-- Menu items with secondary sidebar -->
                <li class="nav-item" v-if="bookmarks.length > 0" @click="openSecondarySidebar('bookmarks', $t('bookmarks'))">
                  <a class="nav-link" :class="{ 'active': secondarySidebarContent === 'bookmarks' }">
                    <i class="nav-icon icon-star"></i> 
                    <span class="nav-text">{{ $t('bookmarks') }}</span>
                  </a>
                </li>
                <li class="nav-item" @click="openSecondarySidebar('integrations', $t('integrations.title'))">
                  <a class="nav-link" :class="{ 'active': secondarySidebarContent === 'integrations' }">
                    <i class="nav-icon icon-puzzle"></i> 
                    <span class="nav-text">{{ $t('integrations.title') }}</span>
                  </a>
                </li>
                <li class="nav-item" @click="openSecondarySidebar('apps', $t('apps'))">
                  <a class="nav-link" :class="{ 'active': secondarySidebarContent === 'apps' }">
                    <i class="nav-icon icon-grid"></i> 
                    <span class="nav-text">{{ $t('apps') }}</span>
                  </a>
                </li>
                
                <li class="nav-divider"></li>
                
                <!-- Regular navigation items -->
                <template v-for="item in customSidebarNav">
                  <template v-if="item.name !== $t('home') && item.name !== $t('dashboard')">
                    <li class="nav-divider" v-if="item.divider" :key="'divider-' + Math.random()"></li>
                    <li class="nav-item" v-else-if="!item.children" :key="item.name">
                      <router-link :to="item.url" class="nav-link" v-if="item.url" @click.native="closeSecondarySidebar">
                        <span class="nav-icon nav-icon--svg" v-if="item.icon && item.icon.startsWith('<svg')" v-html="item.icon"></span>
                        <i :class="['nav-icon', item.icon]" v-else-if="item.icon"></i> 
                        <span class="nav-text">{{ item.name }}</span>
                        <b-badge :variant="item.badge.variant" v-if="item.badge" class="ml-auto">{{ item.badge.text }}</b-badge>
                      </router-link>
                    </li>
                    <li class="nav-item" v-else :key="item.name" @click="openSecondarySidebar(item.originalName || item.name.toLowerCase(), item.name)">
                      <a class="nav-link" :class="{ 'active': secondarySidebarContent === (item.originalName || item.name.toLowerCase()) }">
                        <span class="nav-icon nav-icon--svg" v-if="item.icon && item.icon.startsWith('<svg')" v-html="item.icon"></span>
                        <i :class="['nav-icon', item.icon]" v-else-if="item.icon"></i> 
                        <span class="nav-text">{{ item.name }}</span>
                      </a>
                    </li>
                  </template>
                </template>
              </ul>
            </nav>
          </div>
        </div>
        
        <!-- Secondary sidebar for menu items -->
        <div class="secondary-sidebar" :class="{ 'show': secondarySidebarOpen }" @mouseenter="expandSidebar" @mouseleave="handleSecondarySidebarMouseLeave">
          <div class="secondary-sidebar-header">
            <h6>{{ secondarySidebarTitle }}</h6>
            <button @click="closeSecondarySidebar" class="close-btn">&times;</button>
          </div>
          <div class="secondary-sidebar-body">
            <!-- Components list -->
            <template v-if="secondarySidebarContent === 'components'">
              <b-skeleton-wrapper v-if="!componentsReady">
                <div class="p-3"><b-skeleton></b-skeleton></div>
                <div class="p-3"><b-skeleton width="70%"></b-skeleton></div>
                <div class="p-3"><b-skeleton width="80%"></b-skeleton></div>
              </b-skeleton-wrapper>
              <div v-else-if="componentsMenu.length < 1" class="p-3 text-muted">{{ $t('noComponents') }}</div>
              <a v-else v-for="component in componentsMenu" :key="component.name" 
                @click="openComponent(component)" class="secondary-sidebar-item">
                <SidebarIcon :item="component" />
                {{ component.displayName }}
                <div class="secondary-sidebar-item-actions">
                  <span 
                    v-if="$can('settings ' + component.name.toLowerCase())" 
                    class="sidebar-item-settings"
                    @click.stop="openComponentSettings(component)"
                  >
                    <b-icon icon="gear"></b-icon>
                  </span>
                  <em @click.stop="(e) => bookmarkApp(e, component)"
                    :class="[bookmarks && isBookmarkActive(component.name) ? 'fa fa-star' : 'fa fa-star-o']"></em>
                </div>
              </a>
            </template>

            <!-- Apps list -->
            <template v-if="secondarySidebarContent === 'apps'">
              <b-skeleton-wrapper v-if="!platformAppsMenu && !publicAppsMenu">
                <div class="p-3"><b-skeleton></b-skeleton></div>
                <div class="p-3"><b-skeleton width="70%"></b-skeleton></div>
                <div class="p-3"><b-skeleton width="80%"></b-skeleton></div>
              </b-skeleton-wrapper>
              <template v-else>
                <!-- Platform Apps Section -->
                <template v-if="platformAppsMenu.length > 0">
                  <div class="secondary-sidebar-subheader">{{ $t('platformApps') }}</div>
                  <a v-for="app in platformAppsMenu" :key="app.name" 
                    @click="openApp(app)" class="secondary-sidebar-item">
                    <SidebarIcon :item="app" />
                    {{ app.displayName }}
                    <div class="secondary-sidebar-item-actions">
                      <span
                        v-if="app.type === 'forge-app' && $can('manage forge') && app.status === 'failed'"
                        class="sidebar-item-badge sidebar-item-badge--danger"
                        @click.stop="openAppSettings(app)"
                        v-b-tooltip.hover
                        :title="app.status_message || $t('marketplace.installFailed')"
                      >
                        <b-icon icon="exclamation-triangle-fill"></b-icon>
                      </span>
                      <span
                        v-else-if="app.type === 'forge-app' && $can('manage forge') && app.update_available"
                        class="sidebar-item-badge sidebar-item-badge--warning"
                        @click.stop="openUpdateDialog(app)"
                        v-b-tooltip.hover
                        :title="$t('marketplace.updateAvailable')"
                      >
                        <b-icon icon="arrow-up-circle-fill"></b-icon>
                      </span>
                      <span 
                        v-else-if="app.type === 'forge-app' && $can('manage forge')" 
                        class="sidebar-item-settings"
                        @click.stop="openAppSettings(app)"
                      >
                        <b-icon icon="gear"></b-icon>
                      </span>
                    </div>
                  </a>
                </template>

                <!-- Public Apps Section -->
                <template v-if="publicAppsMenu.length > 0">
                  <div class="secondary-sidebar-subheader">{{ $t('publicApps') }}</div>
                  <a v-for="app in publicAppsMenu" :key="app.name" 
                    @click="openApp(app)" class="secondary-sidebar-item">
                    <i class="icon-globe mr-2"></i>
                    {{ app.displayName }}
                    <div class="secondary-sidebar-item-actions">
                      <span 
                        v-if="$can('manage forge')" 
                        class="sidebar-item-settings"
                        @click.stop="openAppSettings(app)"
                      >
                        <b-icon icon="gear"></b-icon>
                      </span>
                    </div>
                  </a>
                </template>

                <!-- No apps message -->
                <div v-if="platformAppsMenu.length < 1 && publicAppsMenu.length < 1" class="p-3 text-muted">{{ $t('noApps') }}</div>
              </template>
            </template>

            <!-- Integrations list -->
            <template v-if="secondarySidebarContent === 'integrations'">
              <RenderEvent event="menu">
                <template v-slot:header="{ elements }">
                  <div v-if="elements.length < 1" class="p-3 text-muted">{{ $t('noIntegrations') }}</div>
                </template>

                <template v-slot:component="{ context }">
                  <a @click="openIntegration(context)" class="secondary-sidebar-item">
                    <SidebarIcon :item="{ ...context, type: 'integration' }" />
                    {{ $translateLabel(context.label, context.id) }}
                  </a>
                </template>
              </RenderEvent>
            </template>

            <!-- Bookmarks list -->
            <template v-if="secondarySidebarContent === 'bookmarks'">
              <div v-if="bookmarks.length < 1" class="p-3 text-muted">{{ $t('noBookmarks') }}</div>
              <a v-else v-for="bookmark in bookmarks" :key="bookmark.name"
                @click="openBookmark(bookmark)" class="secondary-sidebar-item">
                {{ bookmarkLabelTranslate(bookmark) }}
              </a>
            </template>
            
            <!-- Workflows list -->
            <template v-if="secondarySidebarContent === 'workflow.title'">
              <!-- Static workflow menu items first -->
              <template v-if="getSecondarySidebarItems.length > 0">
                <a v-for="child in getSecondarySidebarItems" :key="child.url"
                  @click="navigateToChild(child)" class="secondary-sidebar-item">
                  <i :class="['mr-2', child.icon]" v-if="child.icon"></i>
                  {{ child.name }}
                </a>
              </template>
              
              <!-- Divider and My Workflows section -->
              <template v-if="workflows.length > 0">
                <div class="secondary-sidebar-subheader">{{ $t('myWorkflows') }}</div>
                <a v-for="workflow in workflows" :key="workflow.url"
                  @click="openWorkflow(workflow)" class="secondary-sidebar-item">
                  <i :class="['mr-2', workflow.icon]" v-if="workflow.icon"></i>
                  {{ workflow.name }}
                </a>
              </template>
              
              <!-- Show no items if both are empty -->
              <div v-if="getSecondarySidebarItems.length < 1 && workflows.length < 1" class="p-3 text-muted">{{ $t('noItems') }}</div>
            </template>
            
            <!-- Dynamic menu items with children -->
            <template v-if="getSecondarySidebarItems.length > 0 && secondarySidebarContent !== 'workflow.title' && secondarySidebarContent !== 'components' && secondarySidebarContent !== 'apps' && secondarySidebarContent !== 'integrations' && secondarySidebarContent !== 'bookmarks'">
              <a v-for="(child, idx) in getSecondarySidebarItems" :key="child.url"
                @click="!child.disabled && navigateToChild(child)" 
                class="secondary-sidebar-item"
                :class="{ 'disabled': child.disabled }"
                :id="`secondary-nav-${child.originalName || idx}`">
                <img :src="child.image" width="16" height="16" class="mr-2" v-if="child.image" />
                <i :class="['mr-2', child.icon]" v-else-if="child.icon"></i>
                {{ child.name }}
                <b-tooltip 
                  v-if="child.tooltip" 
                  :target="`secondary-nav-${child.originalName || idx}`" 
                  placement="right"
                >
                  {{ child.tooltip }}
                </b-tooltip>
              </a>
            </template>
            
            <!-- No items for dynamic menus -->
            <template v-if="getSecondarySidebarItems.length < 1 && secondarySidebarContent !== 'workflow.title' && secondarySidebarContent !== 'components' && secondarySidebarContent !== 'apps' && secondarySidebarContent !== 'integrations' && secondarySidebarContent !== 'bookmarks' && secondarySidebarContent">
              <div class="p-3 text-muted">{{ $t('noItems') }}</div>
            </template>
          </div>
        </div>
        f
        <main class="main">
          <Breadcrumb />
          <div class="container-fluid" style="margin-top:20px">
            <ErrorPage v-if="pageUnavailable" />
            <router-view v-else></router-view>
          </div>
        </main>
      </div>
      <WorkspacesModal 
        :show="showWorkspacesModal" 
        @close="showWorkspacesModal = false"
        @workspaceChanged="handleWorkspaceChanged"
      />
      <TheFooter fixed>
        <!--footer-->

        <div v-if="initialized" class="mr-2" style="z-index: 0">
          <c-switch class="mx-1" color="protokol" variant="pill" :label="true" dataOn="live" dataOff="dev"
            v-model="currentEnv" v-if="this.$can('dev env_access')" />
        </div>

        <div class="clr-protokol-royal-purple">
          <a class="clr-protokol-royal-purple active" href="https://protokol.io">{{ $t('protokol') }}</a>
          <router-link to="/admin/platform/updates">
            <span class="ml-1 clr-protokol-royal-purple active">{{ version }}</span>
          </router-link>
          <span class="ml-1">&copy; {{ getYear }}</span>
        </div>
        
        <div class="ml-auto" v-if="workspace">
          <span style="color: var(--text-secondary);">
            <b-icon icon="building"></b-icon> {{ workspace.name }}
          </span>
        </div>
        <!--End of Tawk.to Script-->

      </TheFooter>
    </template>

    <template v-if="doesntBelongToProject">
      <div class="text-center text-primary my-2">
        <b-spinner class="align-middle"></b-spinner>
      </div>
    </template>

    <InfoCenter />

    <!-- <protokol-tutorial /> -->

    <Onboarding v-if="$isAdmin()" />

    <!-- display when user doesnt belong to  any project -->
    <main-dialog size="lg" v-model="doesntBelongToProject" ok-only ok-title="Logout" @ok="logout"
      @hide="(e) => { e.preventDefault() }">
      <p>{{ $t("notBelongToProject") }}</p>
      <ProjectCreate :xl="12" @created="projectCreated" />
    </main-dialog>
  </div>
  <div v-else class="text-center">
    <b-alert :show="noWorkspace" style="text-align: center" v-if="this.$store.getters.getCurrentProject">{{
      $t("notBelongToWorkspace") }} {{ this.$store.getters.getCurrentProject.name }}. {{ $t("contactAdministrator") }}
      <b-button @click="logout" variant="primary"> {{ $t("logout") }} </b-button> </b-alert>
    <div class="container" v-if="noWorkspace">
      <b-row class="justify-content-center">
        <b-col md="8">

        </b-col>
      </b-row>
    </div>
    </div>

    <!-- Platform Console - Outside main container to always be available -->
    <platform-console />

    <!-- Cortex AI Chat Drawer -->
    <chat-drawer />

    <!-- Kortex Split Panel -->
    <div v-if="hasKortexAccess && isSplitMode && isOpen" class="split-chat-panel" :style="{ width: effectiveSplitChatWidth + 'px' }">
      <div class="split-chat-resize-handle" @mousedown.stop="startSplitResize"></div>
      <chat-panel />
    </div>
  </div>
</template>

<style lang="scss">
.kortex-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  background: rgba(108, 128, 255, 0.08);
  border: 1px solid rgba(108, 128, 255, 0.22);
  border-radius: 8px;
  color: rgba(108, 128, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(108, 128, 255, 0.14);
    border-color: rgba(108, 128, 255, 0.4);
    color: #8fa0ff;
    box-shadow: 0 0 12px rgba(108, 128, 255, 0.15);
  }

  &:active {
    transform: scale(0.97);
  }
}

.kortex-btn__icon {
  opacity: 0.85;
}

.kortex-btn__label {
  letter-spacing: 0.01em;
}

.kortex-kbd-hint {
  display: inline-block;
  margin-left: 2px;
  padding: 1px 5px;
  font-size: 10px;
  font-family: inherit;
  line-height: 1.4;
  color: rgba(108, 128, 255, 0.55);
  background: rgba(108, 128, 255, 0.06);
  border: 1px solid rgba(108, 128, 255, 0.15);
  border-radius: 4px;
  vertical-align: middle;
  letter-spacing: 0.02em;
  pointer-events: none;
}

@keyframes kortex-helper-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(108, 128, 255, 0); border-color: rgba(108, 128, 255, 0.22); }
  50%       { box-shadow: 0 0 10px 3px rgba(108, 128, 255, 0.25); border-color: rgba(108, 128, 255, 0.7); }
}

@keyframes kortex-spark-float {
  0%   { opacity: 0; transform: translateY(0) scale(0.6); }
  40%  { opacity: 1; transform: translateY(-4px) scale(1); }
  100% { opacity: 0; transform: translateY(-8px) scale(0.8); }
}

.kortex-helper-active {
  animation: kortex-helper-glow 2s ease-in-out infinite !important;
}

.kortex-helper-spark {
  position: absolute;
  top: -6px;
  right: -4px;
  font-size: 10px;
  color: #a78bfa;
  animation: kortex-spark-float 2s ease-in-out infinite;
  pointer-events: none;
  line-height: 1;
}

.switch-protokol .switch-input:checked+.switch-slider {
  background: linear-gradient(135deg, #6c80ff, #764ba2);
  border: none;
}

.workspaceMarker {
  background-color: var(--text-primary);
  padding: 10px;
}

.workspaceMarkerActive {
  height: 50px;
}

.notification-bell {
  margin-right: 10px;
}

.dark-mode {
  .navbar-nav .nav-link:hover {
    color: #ccc;
  }

  .navbar-nav .nav-link.active {
    color: #ccc;
  }
}

.bookmark-item {
  // border-left: 1px solid rgba(255,255,255,0.5);
  white-space: nowrap;
}

a.dropdown-item i.fa.fa-star-o {
  margin-left: 10px;
}

.fa-home {
  &:hover {
    color: #6c80ff;
  }

  cursor: pointer;
}

a.icon-holder {
  right: 10px;
  top: 5px;
  position: absolute;
  font-size: 20px;
  padding-left: 10px;
  padding-right: 10px;
  z-index: 10;
}

.sidebar-divider {
  display: block !important;
  background-color: var(--bg-card);
}

.navbar.navbar-bookmarks {
  width: 85%;
}

.navbar-expand .navbar-nav-bookarmarks.navbar-nav {
  width: 100%;
}

.navbar-bookmarks {
  padding: 0px;
}

.navbar-nav-bookmarks {
  overflow: scroll;
  scrollbar-width: none;
}

.navbar-nav-bookmarks::-webkit-scrollbar {
  display: none;
}

/* Custom Sidebar Styles */
.custom-sidebar {
  position: fixed;
  top: 55px;
  left: 0;
  width: 50px;
  height: calc(100vh - 55px);
  background: var(--bg-card);
  z-index: 1019;
  overflow: hidden;
  transition: width 0.3s ease;
  
  &.expanded {
    width: 200px;
  }
}

/* Secondary Sidebar Styles */
.sidebar-custom-nav {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  width: 200px;
  
  .nav {
    padding: 0;
    margin: 0;
    list-style: none;
  }
  
  .nav-item {
    cursor: pointer;
    width: 100%;
    
    &:hover > .nav-link {
      background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
      color: var(--text-on-button);
      
      .nav-icon {
        color: var(--text-on-button);
      }
    }
    
    .nav-link {
      display: flex;
      align-items: center;
      padding: 0.75rem 1rem;
      color: var(--text-primary);
      text-decoration: none;
      transition: background-color 0.2s;
      white-space: nowrap;
      width: 200px;
      
      &:hover {
        background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
        color: var(--text-on-button);
        text-decoration: none;
        
        .nav-icon {
          color: var(--text-on-button) !important;
        }
      }
      
      &.active {
        background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
        color: var(--text-on-button);
        
        .nav-icon {
          color: var(--text-on-button) !important;
        }
      }
      
      &.router-link-active {
        background: var(--border-default);
        color: var(--text-primary);
      }
      
      .nav-icon {
        width: 20px;
        min-width: 20px;
        text-align: center;
        transition: color 0.2s;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        margin-right: 1rem;
        
        &::before {
          margin: 0;
        }
        
        &.nav-icon--svg svg {
          width: 18px;
          height: 18px;
        }
      }
      
      .nav-text {
        flex: 1;
        white-space: nowrap;
        opacity: 1;
        transition: opacity 0.3s ease;
      }
    }
  }
  
  .nav-divider {
    height: 1px;
    background: var(--border-default);
    margin: 0.5rem 0;
  }
}

.custom-sidebar:not(.expanded) {
  .nav-text {
    opacity: 0;
  }
}

.main {
  margin-left: 45px !important;
  transition: margin-left 0.3s ease;
}

.app-footer {
  margin-left: 50px !important;
  transition: margin-left 0.3s ease;
}

.secondary-sidebar {
  position: fixed;
  top: 55px;
  left: 50px;
  width: 280px;
  height: calc(100vh - 55px);
  background: var(--form-bg);
  box-shadow: inset 12px 0 12px -8px rgba(0, 0, 0, 0.15);
  transform: translateX(-100%);
  transition: transform 0.3s ease, left 0.3s ease;
  z-index: 1018;
  display: flex;
  flex-direction: column;
}

.custom-sidebar.expanded + .secondary-sidebar {
  left: 200px;
}

.secondary-sidebar.show {
  transform: translateX(0);
}

.secondary-sidebar-header {
  padding: 1rem;
  border-bottom: 1px solid #3a4149;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.secondary-sidebar-header h6 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
}

.secondary-sidebar-header .close-btn {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  opacity: 0.7;
  line-height: 1;
  padding: 0;
  width: 24px;
  height: 24px;
}

.secondary-sidebar-header .close-btn:hover {
  opacity: 1;
}

.secondary-sidebar-body {
  flex: 1;
  overflow-y: auto;
}

.secondary-sidebar-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: var(--text-primary);
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--border-separator);
  
  /* Ensure proper icon spacing */
  .sidebar-icon-wrapper {
    flex-shrink: 0;
  }
  
  .secondary-sidebar-item-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    
    em {
      opacity: 0.6;
      transition: opacity 0.2s;
      
      &.fa-star {
        opacity: 1;
      }
    }
  }
  
  .sidebar-item-settings {
    opacity: 0;
    transition: opacity 0.2s ease;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    
    &:hover {
      opacity: 1 !important;
      background: var(--border-default);
      color: var(--text-primary);
    }
    
    svg {
      font-size: 14px;
    }
  }

  .sidebar-item-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    cursor: pointer;
    flex-shrink: 0;

    svg {
      font-size: 11px;
      color: white;
    }

    &--danger {
      background: #dc3545;
      box-shadow: 0 2px 6px rgba(220, 53, 69, 0.45);

      &:hover {
        background: #c82333;
      }
    }

    &--warning {
      background: #fd7e14;
      box-shadow: 0 1px 4px rgba(253, 126, 20, 0.4);
      animation: sidebar-pulse-orange 2s infinite;

      &:hover {
        background: #e8690a;
        transform: scale(1.1);
      }
    }
  }

  @keyframes sidebar-pulse-orange {
    0%, 100% { box-shadow: 0 1px 4px rgba(253, 126, 20, 0.2); }
    50%       { box-shadow: 0 1px 6px rgba(253, 126, 20, 0.5); }
  }
  
  &:hover {
    .sidebar-item-settings {
      opacity: 0.7;
    }
    
    .secondary-sidebar-item-actions em {
      opacity: 1;
    }
  }
}

.secondary-sidebar-item:hover {
  background-color: var(--border-default);
  color: var(--text-primary);
  text-decoration: none;
}

.secondary-sidebar-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: auto;
}

.secondary-sidebar-item.disabled:hover {
  background-color: transparent;
}

.secondary-sidebar-item em {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.secondary-sidebar-item:hover em,
.secondary-sidebar-item em.fa-star {
  opacity: 1;
}

.secondary-sidebar-subheader {
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-muted);
  letter-spacing: 0.5px;
  margin-top: 0.5rem;
  border-top: 1px solid var(--border-separator);
}

/* Icon colors for developer tools */
.secondary-sidebar-item .icon-fire {
  color: orange;
}

.secondary-sidebar-item .icon-energy {
  color: yellow;
}



/* Adjust main content when secondary sidebar is open */
.sidebar-lg-show .secondary-sidebar {
  left: 200px;
}

.sidebar-minimized .secondary-sidebar {
  left: 50px;
}

/* Override CoreUI's default 200px margin - adjust for custom sidebar */
@media (min-width: 992px) {
  .sidebar-lg-show.sidebar-fixed .main,
  .sidebar-show.sidebar-fixed .main {
    margin-left: 45px !important;
  }

  .sidebar-lg-show.sidebar-fixed .app-footer,
  .sidebar-show.sidebar-fixed .app-footer {
    margin-left: 0px !important;
  }
}

.app.secondary-sidebar-open .main {
  margin-left: 480px;
  transition: margin-left 0.3s ease;
}

.sidebar-minimized.secondary-sidebar-open .main {
  margin-left: 330px;
}

/* Dark mode adjustments */
.dark-mode .secondary-sidebar {
  background: var(--form-bg);
  box-shadow: inset 12px 0 12px -8px rgba(0, 0, 0, 0.25);
}

.dark-mode .secondary-sidebar-header {
  border-bottom-color: var(--border-default);
}

/* ── Split-screen chat layout ── */
.split-chat-panel {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1029;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--border-default, #2a2d3a);
  background: #1a1d27;
  overflow: hidden;
}

.split-chat-resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  z-index: 10;
  background: transparent;
  transition: background 0.15s;

  &:hover {
    background: rgba(108, 128, 255, 0.2);
  }
}
</style>

<script>
import nav from '@/_nav'
import { Header as AppHeader, SidebarToggler, Sidebar as AppSidebar, SidebarFooter, SidebarForm, SidebarHeader, SidebarMinimizer, SidebarNav, Breadcrumb, Aside as AppAside, AsideToggler, Footer as TheFooter } from '@/components/ui/vue/src'
import Projects from './Projects'
import WorkspacesModal from '../components/workflow/WorkspacesModal'
import DefaultHeaderDropdownAccnt from './DefaultHeaderDropdownAccnt'
import ProjectCreate from '../views/projects/Create'
import Changelog from '../../changelog.json'
import { Switch as cSwitch } from '@coreui/vue'
import Input from '@/components/form/Input'
import _ from 'lodash'
import { Howl } from 'howler';
import RenderEvent from '../components/integrations/RenderEvent'
import ErrorPage from '../views/pages/Page500'
import InfoCenter from '@/views/dashboard/InfoCenter'
import Onboarding from '@/views/dashboard/Onboarding'
import SidebarIcon from '@/components/elements/SidebarIcon'
import PlatformConsole from '@/components/console/PlatformConsole'
import UnpaidInvoiceBanner from '@/components/billing/UnpaidInvoiceBanner'
import HeaderAlerts from '@/components/alerts/HeaderAlerts'
import ChatDrawer from '@/views/cortex/ChatDrawer'
import ChatPanel from '@/views/cortex/ChatPanel'
import { openForgeAppWithToken } from '@/helpers/forgeTokenLaunch'

var DEFAULT_SPLIT_CHAT_WIDTH = 880
var MIN_SPLIT_CHAT_WIDTH = 720
var MAX_SPLIT_CHAT_VIEWPORT_RATIO = 0.72

function clampSplitChatWidth (width, viewportWidth) {
  var maxWidth = Math.floor(viewportWidth * MAX_SPLIT_CHAT_VIEWPORT_RATIO)
  if (maxWidth < MIN_SPLIT_CHAT_WIDTH) {
    return maxWidth
  }
  return Math.max(MIN_SPLIT_CHAT_WIDTH, Math.min(maxWidth, width))
}

export default {
  name: 'DefaultContainer',
  components: {
    AsideToggler,
    AppHeader,
    AppSidebar,
    AppAside,
    TheFooter,
    Projects,
    WorkspacesModal,
    Breadcrumb,
    DefaultHeaderDropdownAccnt,
    SidebarForm,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    SidebarNav,
    SidebarMinimizer,
    ProjectCreate,
    cSwitch,
    InputField: Input,
    RenderEvent,
    ErrorPage,
    InfoCenter,
    Onboarding,
    SidebarIcon,
    PlatformConsole,
    UnpaidInvoiceBanner,
    HeaderAlerts,
    ChatDrawer,
    ChatPanel
  },
  data() {
    return {
      showWorkspacesModal: false,
      secondarySidebarOpen: false,
      secondarySidebarContent: null,
      secondarySidebarTitle: '',
      ready: false,
      componentsReady: false,
      doesntBelongToProject: false,
      noWorkspace: false,
      projectInfo: null,
      appUrl: null,
      createWorkspace: {
        modal: false,
        name: ''
      },
      errors: [],
      menu: [],
      initialized: false,
      subscriptionAlert: null,
      sidebarHoverTimeout: null,
      sidebarExpanded: false,
      splitChatWidth: DEFAULT_SPLIT_CHAT_WIDTH,
      splitViewportWidth: typeof window !== 'undefined' ? window.innerWidth : Math.ceil(DEFAULT_SPLIT_CHAT_WIDTH / MAX_SPLIT_CHAT_VIEWPORT_RATIO),
      isSplitResizing: false,
      splitResizeStartX: 0,
      splitResizeStartWidth: 0,
      helperActive: false,
    }
  },
  computed: {
    isSplitMode: function () {
      return this.$store.state.cortex.isSplitMode
    },
    isOpen: function () {
      return this.$store.state.cortex.isOpen
    },
    effectiveSplitChatWidth: function () {
      return clampSplitChatWidth(this.splitChatWidth, this.splitViewportWidth)
    },
    hasKortexAccess: function () {
      if (!this.$store.getters.isIntegrationActive('protokol-kortex')) return false
      if (this.$isAdmin()) return true
      return this.$store.state.cortex.myAccess !== null
    },
    pageUnavailable() {
      return this.$route.query.pageUnavailable !== undefined
    },

    getYear() {
      return new Date().getFullYear()
    },

    version() {
      return Changelog[0].version
    },
    settingsPath() {
      return this.$route.meta.settingsPath
    },

    containerKey() {
      return this.$store.getters.containerKey()
    },

    bookmarks: {
      get() {
        let bookmarks = _.get(this.$store.getters.projectUserConfig("bookmarks"), "data", {})

        return Object.values(bookmarks).filter(b => {
          let component = this.$store.getters.componentsMenu.find(f => {
            return b.name == f.name
          })

          if (!component) return false

          if (!component.is_active) {
            return false
          }

          if (component.is_public && this.$store.getters.isLiveEnv) {
            return true
          }

          return !this.$store.getters.isLiveEnv
        })
      },

      set(val) {
        let bookmarks = {}
        Object.values(val).forEach(v => {
          bookmarks[v.name] = v
        })

        this.$store.dispatch('updateProjectUserConfig', { path: "bookmarks", data: bookmarks })
      }
    },

    name() {
      return this.$route.name
    },
    list() {
      return this.$route.matched.filter((route) => {
        return route.name || route.meta.label
      })
    },

    workspaces() {
      return _.get(this.$store.getters.getCurrentProject, 'workspaces', []).filter(w => {
        return w.uuid != this.$store.getters.getCurrentWorkspace.uuid
      })
    },

    workspace() {
      return this.$store.getters.getCurrentWorkspace
    },

    sidebarNav() {
      const account = this.$store.getters.getAccount
      
      let i = _.cloneDeep(nav.items).map(i => {
        i.originalName = i.name // Keep original key
        i.name = this.$t(i.name.toLowerCase())
        if (i.children) {
          i.children = i.children.map(c => {
            c.originalName = c.name // Keep original key for children
            c.name = this.$t(c.name.toLowerCase())
            
            // Mark children as disabled based on account limits
            if (account) {
              if (c.originalName === 'forge' && (!account.forge || account.forge <= 0)) {
                c.disabled = true
                c.tooltip = this.$t('featureNotAvailableOnAccount')
              }
            }
            
            return c
          })
        }

        return i
      })

      if (this.$can("manage base_components") && !this.currentEnv) {
        i.push({
          name: this.$t('add'),
          url: '/admin/components/create',
          icon: 'icon-plus'
        })
      }

      i = i.filter(i => {
        if (this.currentEnv && i.onlyDev) {
          return false
        }

        if (i.permission && i.permission == "admin") {
          if (!this.$isAdmin()) {
            return false
          }
        } else if (i.permission && !this.$can(i.permission)) {
          return false
        }

        if (i.integration && !this.$store.getters.isIntegrationActive(i.integration)) {
          return false
        }

        return true
      })

      return i
    },
    
    customSidebarNav() {
      return this.sidebarNav
    },
    
    getSecondarySidebarItems() {
      if (!this.secondarySidebarContent) return []
      
      // Find the item in sidebarNav that matches the current secondary sidebar content
      const navItem = this.sidebarNav.find(item => 
        (item.originalName && item.originalName === this.secondarySidebarContent) ||
        (item.name && item.name.toLowerCase() === this.secondarySidebarContent)
      )
      
      if (!navItem || !navItem.children) return []

      return navItem.children.filter(child => {
        if (child.permission && child.permission === 'admin') {
          return this.$isAdmin()
        }
        if (child.permission && !this.$can(child.permission)) {
          return false
        }
        return true
      })
    },

    appsMenu() {
      let apps = this.$store.getters.getApps || []

      const platformApps = apps.filter(c => {
        if (this.$store.getters.getCurrentEnv == 'dev')
          return c.dev_version != ''

        if (this.$store.getters.getCurrentEnv == 'live')
          return c.public_version != ''
      }).map(c => {
        c.type = "platform-app"
        c.displayName = c.name
        if (c.label) {
          c.displayName = this.$translateLabel(c.label) || c.name
        }

        c.displayName = _.truncate(c.displayName, { length: 15 })

        c.url = "/app/" + c.uuid

        if (this.$store.getters.getCurrentEnv == 'dev') {
          if (c.public_version != '') {
            c.badge = {
              text: "live",
              variant: "success"
            }
          } else {
            c.badge = {
              text: "dev"
            }
          }
        }

        return c
      })

      const forgeApps = (this.$store.getters.getForgeApps || []).map(app => {
        app.type = "forge-app"
        app.displayName = app.name
        if (app.label) {
          app.displayName = this.$translateLabel(app.label) || app.name
        }
        app.displayName = _.truncate(app.displayName, { length: 15 })
        return app
      })

      const platformForgeApps = (this.$store.getters.getForgeApps || []).filter(app => app.type !== 'public').map(app => {
        app.type = "forge-app"
        app.displayName = app.name
        if (app.label) {
          app.displayName = this.$translateLabel(app.label) || app.name
        }
        app.displayName = _.truncate(app.displayName, { length: 15 })
        return app
      })

      return platformApps.concat(platformForgeApps)
    },

    platformAppsMenu() {
      return this.appsMenu
    },

    publicAppsMenu() {
      const publicForgeApps = (this.$store.getters.getAllForgeApps || []).filter(app => app.type === 'public').map(app => {
        const originalType = app.type
        app.type = "forge-app"
        app.originalType = originalType  // Preserve the original type
        app.displayName = app.name
        if (app.label) {
          app.displayName = this.$translateLabel(app.label) || app.name
        }
        app.displayName = _.truncate(app.displayName, { length: 15 })
        return app
      })

      return publicForgeApps
    },

    componentsMenu() {
      let permissions = this.$store.getters.permissions || []

      let componentsMenu = this.$store.getters.componentsMenu.filter(c => {
        return c.is_public
      }).map(c => {
        c.type = 'component'
        c.displayName = c.name
        if (c.label) {
          c.displayName = this.$translateLabel(c.label, c.name)
        }

        c.displayName = _.truncate(c.displayName, { length: 15 })

        if (c.is_external) {
          listUrl = '/admin/component/external/' + c.uuid
          settingsUrl = '/admin/component/external/' + c.uuid + '/settings'
        }

        // Preserve icon for sidebar display
        // Icon is already part of component object from store
        
        delete c.label;

        return c
      })

      let m = this.menu
      m = componentsMenu;
      m.sort((a, b) => {
        return a.sort - b.sort
      })

      return m
    },

    integrations() {
      try {
        let sidebar = this.$store.getters.getIntegrations.filter(i => {
          return i.template.renderevents.indexOf("page") !== -1
        }).map(i => {
          return {
            name: this.$translateLabel(i.label),
            url: '/admin/integrations/page/' + i.id,
            icon: 'icon-menu',
            attributes: {
              exact: true
            },
          }
        })

        return sidebar
      } catch (err) {

        this.$bvToast.toast(this.$t('failedToLoadIntegrations'), {
          variant: 'danger',
          title: "Error",
          autoHideDelay: 10000
        })

        this.$debugLog(err)

        return []
      }
    },

    workflows() {
      try {
        let sidebar = this.$store.getters.getWorkflows.map(i => {
          return {
            name: i.name,
            url: "/admin/workflow/" + i.uuid,
            icon: 'icon-share',
            attributes: {
              exact: true,
            },
          }
        })

        return sidebar
      } catch {
        this.$bvToast.toast(this.$t('failedToLoadWorkflows'), {
          variant: 'danger',
          title: "Error",
          autoHideDelay: 10000
        })

        return []
      }
    },

    serverError() {
      return {
        comment: '',
        request_uuid: this.$store.getters.errorRequestID
      }
    },

    accountExpiresAt() {
      let user = this.$store.getters.getUser;

      if (user.expires_at === null || user.expires_at == "") {
        return null
      }

      return user.expires_at;
    },

    subscriptions() {
      return this.$store.getters.getSubscriptions
    },

    currentEnv: {
      get() {
        return this.$store.getters.getCurrentEnv == 'live'
      },

      set(val) {
        this.$store.dispatch("setCurrentEnv", val ? "live" : "dev").then(() => {
          this.reRender()
          if (this.$store.getters.getCurrentEnv == 'dev')
            this.$store.dispatch("initTutorial", "dev")
        })
      }
    },

    currentTheme: {
      get() {
        return this.$store.getters.getCurrentTheme == 'dark'
      },

      set(val) {
        this.$store.dispatch("setCurrentTheme", val ? "dark" : "light")
      }
    },

    currentWorkspace: {
      get() {
        return this.$store.getters.getCurrentWorkspace
      },

      set(val) {
        this.$store.dispatch("setWorkspace", val).then(() => {
          this.reRender()
        })
      }
    },

    currentWorkspaceName() {
      if (this.currentWorkspace !== undefined)
        return _.truncate(this.currentWorkspace.name, { length: 10 })

      return null
    },

    currentProjectName() {
      if (this.$store.getters.getCurrentProject) {
        return _.truncate(this.$store.getters.getCurrentProject.name, { length: 15 })
      }
    },

    newEvents() {
      return this.$store.getters.newEventsCount
    },
  },

  watch: {
    '$route.path': function(path) {
      if (!path) return

      if (path.indexOf('/admin/dashboard') === 0 || path.indexOf('/admin/workflow') === 0) {
        this.$store.dispatch("fetchWorkflows", { force: true }).catch(err => {
          this.$debugLog(err)
        })
      }
    }
  },

  methods: {
    startSplitResize: function (e) {
      this.isSplitResizing = true
      this.updateSplitViewportWidth()
      this.splitResizeStartX = e.clientX
      this.splitResizeStartWidth = this.splitChatWidth
      document.addEventListener('mousemove', this.onSplitResize)
      document.addEventListener('mouseup', this.stopSplitResize)
    },
    onSplitResize: function (e) {
      if (!this.isSplitResizing) return
      var delta = this.splitResizeStartX - e.clientX
      var newWidth = this.splitResizeStartWidth + delta
      this.splitChatWidth = clampSplitChatWidth(newWidth, this.splitViewportWidth)
    },
    stopSplitResize: function () {
      this.isSplitResizing = false
      document.removeEventListener('mousemove', this.onSplitResize)
      document.removeEventListener('mouseup', this.stopSplitResize)
    },
    updateSplitViewportWidth: function () {
      if (typeof window !== 'undefined') {
        this.splitViewportWidth = window.innerWidth
      }
    },
    handleConsoleKeyboard(e) {
      // Cmd+Shift+C → toggle platform console
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault()
        this.$store.dispatch('console/toggle')
      }
    },

    reRender() {
      //let popup = this.$loadingPopup()
      this.$store.dispatch('refreshState')
      this.$store.commit("RELOAD_CONTAINER")
      this.$router.push('/admin/dashboard')
      //popup.hide()
    },

    openCortexChat: function () {
      if (!this.hasKortexAccess) return
      this.$store.dispatch('cortex/openChat')
    },

    handleWorkspaceChanged(workspace) {
      this.reRender()
    },
    
    expandSidebar() {
      if (this.sidebarHoverTimeout) {
        clearTimeout(this.sidebarHoverTimeout)
        this.sidebarHoverTimeout = null
      }
      this.sidebarExpanded = true
    },
    
    minimizeSidebar() {
      this.sidebarHoverTimeout = setTimeout(() => {
        this.sidebarExpanded = false
      }, 100)
    },
    
    handleSidebarMouseLeave(event) {
      // Check if mouse is moving to the secondary sidebar
      const secondarySidebar = document.querySelector('.secondary-sidebar')
      if (secondarySidebar && secondarySidebar.classList.contains('show')) {
        // Don't minimize yet if secondary sidebar is open
        return
      }
      this.minimizeSidebar()
    },
    
    handleSecondarySidebarMouseLeave(event) {
      // Close secondary sidebar and minimize when leaving
      this.closeSecondarySidebar()
      this.minimizeSidebar()
    },
    
    openSecondarySidebar(content, title) {
      this.secondarySidebarContent = content
      this.secondarySidebarTitle = title
      this.secondarySidebarOpen = true

      if (content === 'workflow.title') {
        this.$store.dispatch("fetchWorkflows", { force: true }).catch(err => {
          this.$debugLog(err)
        })
      }
    },
    
    closeSecondarySidebar() {
      this.secondarySidebarOpen = false
      this.secondarySidebarContent = null
    },
    
    handleSidebarMouseLeave() {
      // Check if mouse is moving to the secondary sidebar
      const secondarySidebar = document.querySelector('.secondary-sidebar')
      if (secondarySidebar && secondarySidebar.classList.contains('show')) {
        // Don't minimize yet if secondary sidebar is open and we might move to it
        return
      }
      this.minimizeSidebar()
      if (this.secondarySidebarOpen) {
        this.closeSecondarySidebar()
      }
    },
    
    toggleDropdown(item) {
      this.$set(item, 'open', !item.open)
    },
    
    navigateToChild(child) {
      if (child.url) {
        this.$router.push(child.url)
        this.closeSecondarySidebar()
      }
    },

    changeWorkspace(workspace) {
      this.currentWorkspace = workspace
    },

    logout() {
      this.$http.post("/v1/user/logout").then(resp => {
        this.$router.go()
      })
    },

    displayDataChanged(data) {
      let currentComponentUUID = this.$store.getters.currentComponent.uuid
      let currentUser = this.$store.getters.getUser

      if ((data.data.component.uuid == currentComponentUUID)
        && data.user.uuid !== currentUser.uuid
      ) {

        this.$bvToast.toast("Data Changed", {
          variant: 'primary',
          autoHideDelay: 10000 * 10000
        })
      }
    },

    sendErrorReport() {
      this.$http.post("/v1/system/report", this.serverError).then(resp => {
        this.$mainDialog.msgBoxOk(this.$t("thanksFeedback"))
      })
    },

    projectCreated() {
      this.$router.go('/admin/dashboard');
    },

    fieldError(error) {
      let found = this.errors.find((i, e) => {
        return i.field == error.field
      })

      if (found === undefined) {
        this.errors.push(error)
      } else {
        this.errors.map(e => {
          if (found.field == e.field)
            e.error = error.error
        })
      }
    },

    checkForErrors() {
      return this.errors.find((i, e) => {
        return i.error
      })
    },

    playNotification() {
      let notificationSettings = this.$store.getters.userConfig("notification_settings");
      if (!notificationSettings) return

      if (notificationSettings.data.play_sound) {
        var sound = new Howl({
          src: ['../sound/notification.wav']
        });

        sound.play();
      }
    },

    openComponent(component) {
      let ref = component.name
      if (component.scope) {
        ref = `${component.scope}/${ref}`
      }

      this.$router.push(`/admin/component/${ref}`)
    },

    openComponentSettings(component) {
      let ref = component.name
      if (component.scope) {
        ref = `${component.scope}/${ref}`
      }

      if (component.is_external) {
        this.$router.push(`/admin/component/external/${component.uuid}/settings`)
      } else {
        this.$router.push(`/admin/component/${ref}/settings`)
      }
      this.closeSecondarySidebar()
    },

    async openApp(app) {
      if (app.type == "platform-app") {   
        window.open(this.$router.resolve(`/app/${app.name}`).href, "_blank")
      } else if (app.type == "forge-app") {
        // Check if this is a public app
        if (app.originalType === 'public' && app.config && app.config.domain && app.config.domain.basehostname) {
          window.open(`https://${app.config.domain.basehostname}`, "_blank")
          return
        }
        
        // Default forge app behavior
        let url = this.$env.get("FORGE_APP_URL_PATTERN").replaceAll("{name}", app.name)
        url = `${url}/launch?env=${this.$store.getters.getCurrentEnv}&lang=${this.$store.getters.getLocale}&name=${app.name}&uuid=${app.uuid}`

        if (url.startsWith("http://localhost")) {
          url = `${url}&name=${app.name}`
        }

        openForgeAppWithToken(this, url, app.name)
      } else {
        // Fallback for apps without type
        window.open(this.$router.resolve(`/app/${app.name}`).href, "_blank")
      }
    },

    openAppSettings(app) {
      if (app.type == "platform-app") {
        this.$router.push(`/admin/app/${app.uuid}/settings`)
      } else if (app.type == "forge-app") {
        this.$router.push(`/admin/forge/${app.uuid}`)
      }
      this.closeSecondarySidebar()
    },

    openUpdateDialog(app) {
      if (app.type == "forge-app") {
        this.$router.push(`/admin/forge/${app.uuid}`)
      }
      this.closeSecondarySidebar()
    },

    isBookmarkActive(name) {
      return !!this.bookmarks.find(b => {
        return b.name == name
      });
    },

    bookmarkApp(e, app) {
      e.stopImmediatePropagation();
      let data = this.bookmarks || {};
      let bookmark = data.find(d => {
        return d.name == app.name
      })

      if (bookmark) {
        data = data.filter(d => {
          return d.name != bookmark.name
        })
      } else {
        data.push({
          name: app.name,
          url: app.url,
          label: app.label
        })
      }

      this.bookmarks = data
    },

    openBookmark(bookmark) {
      this.$router.push(bookmark.url)
    },

    openIntegration(int) {
      this.$router.push(`/integrations/page/${int.id}`)
    },
    
    openWorkflow(workflow) {
      console.log("open workflow", workflow)
      this.$router.push(workflow.url)
    },

    bookmarkLabelTranslate(bookmark) {
      try {
        let component = this.$store.getters.components.find(c => {
          return c.name == bookmark.name
        })

        return this.$translateLabel(component.label, bookmark.name)
      } catch {
        // do nothing
        return bookmark.name
      }
    },

    goToHome() {
      this.$router.push("/")
    }
  },

  async destroyed() {
    this.$eventBus.close()
  },

  async created() {
    const popup = this.$loadingPopup()

    try {
      await this.$setupComponents()

      this.componentsReady = true
      this.noWorkspace = false
      this.initialized = true
    } catch (err) {
      console.error(err)  

      if (err.response !== undefined) {
        if (err.response.status == 400) {
          this.doesntBelongToProject = true
          return
        }

        if (err.response.status >= 500) {
          this.$bvToast.toast(this.$t('unexpectedError'), {
            variant: 'danger'
          })

          return
        }

        if (err.response.status == 403) {
          this.componentsReady = true
        }
      }

      if (err == "noWorkspace" && this.$store.getters.currentWorkspace == null) {
        this.noWorkspace = true
        return
      }

      this.$bvToast.toast(this.$t('unexpectedError'), {
        variant: 'danger'
      })
    } finally {
      popup.hide()
    }
  },

  async mounted() {
    this.updateSplitViewportWidth()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateSplitViewportWidth)
    }

    this._onHelperChanged = () => { this.helperActive = !!(window.kortex && window.kortex._helperActive) }
    window.addEventListener('kortex:helper-changed', this._onHelperChanged)

    await this.$store.dispatch("fetchPermissions")

    if (!this.$can('dev env_access')) {
      this.$store.dispatch("setCurrentEnv", "live")
    }

    if (!this.$can('dashboard access')) {
      this.$router.push("/")
      return
    }

    // Fetch all forge apps for public apps section
    this.$store.dispatch("fetchAllForgeApps").catch(err => {
      console.error("Failed to fetch all forge apps:", err)
    })
    
    this.$store.dispatch("fetchUserConfigs").then(() => {
      let data = this.$store.getters.userConfig('locale')
      let savedLocale = _.get(data[0], "data.locale", 'en_US')

      this.$store.commit('SET_LOCALE', savedLocale)
      this.$root.$i18n.locale = savedLocale
    }).catch(err => {
      console.log(err)
    })

    if (this.$isAdmin()) {
      this.$store.dispatch("fetchPendingInvoices").catch(function () {})
    }

    this.$store.dispatch("fetchSubscriptions").then(data => {
      let expired = data.filter(d => {
        return d.is_expired
      })

      let cancelled = data.filter(d => {
        return d.is_canceled
      })

      if (cancelled.length > 0) {
        let msg = this.$t("cancelledSubscriptions", {
          services: cancelled.map(c => {
            return c.license_product.name
          }).join(",")
        })

        this.$bvToast.toast(msg, {
          variant: 'warning',
          title: "Warning",
          autoHideDelay: 10000
        })
      }

    }).catch(err => {
      this.$store.commit("SET_SUBSCRIPTIONS", [])
    })

    this.$store.dispatch("fetchIntegrations")
    this.$store.dispatch("fetchWorkflows", { force: true }).catch(err => {
      this.$debugLog(err)
    })
    //this.$store.dispatch("fetchSatellites");
    this.$store.dispatch("fetchInfoMessages").catch(err => {
      this.$debugLog(err)
    })

    localStorage.setItem("run_mode", "platform")
    localStorage.setItem("current_theme", "dark")

    this.$store.dispatch("fetchNewEventsCount")

    this.$store.commit("SET_CURRENT_CONTEXT", "dashboard")

    this.$store.dispatch("initTutorial", "dashboard")
    if (this.$store.getters.getCurrentEnv == 'dev')
      this.$store.dispatch("initTutorial", "dev")

    if (!this.$isAdmin()) {
      this.$store.dispatch('cortex/loadMyAccess').catch(function () {})
    }

  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.onSplitResize)
    document.removeEventListener('mouseup', this.stopSplitResize)
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateSplitViewportWidth)
      window.removeEventListener('kortex:helper-changed', this._onHelperChanged)
    }
  }
}
</script>
